import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, User, AlertCircle, Users, CheckCircle2, ShieldCheck,
  X, Plus, Edit2, Trash2, History, Save, ChevronDown, ChevronUp,
  Clock, UserPlus
} from 'lucide-react';

import Header from '../components/Header';
import Footer from '../components/Footer';
import ResolvedAlertsModal from '../components/ResolvedAlertsModal';

import { useAuth } from '../context/AuthContext';

import { getToken } from '../utils/auth';
import { parseBackendDate } from '../utils/datetime';
import Modal, { DialogHeader, DialogFooter, DialogBody } from '../components/ui/Modal';
import { AnimatedNumber } from '../motion';
import InlineLoading from '../components/ui/InlineLoading';

const priorityRank = {
  'Normal': 0,
  'Low': 1,
  'Moderate': 2,
  'High': 3,
  'Critical': 4
};

const AlertsDashboard = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [alerts, setAlerts] = useState([]);
  const [resolvedAlerts, setResolvedAlerts] = useState([]);
  const [patients, setPatients] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const [hoveredPatient, setHoveredPatient] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, showAbove: false });
  const [filters, setFilters] = useState({
    patientname: '',
    alertDate: '',
    assignedTo: ''
  });

  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertHistory] = useState({ assignment_history: [] });
  const [selectedDoctorToAdd, setSelectedDoctorToAdd] = useState('');

  const [expandedTasks, setExpandedTasks] = useState({});
  const [newRecommendation, setNewRecommendation] = useState({});
  const [newNote, setNewNote] = useState({});
  const [editingRecommendation, setEditingRecommendation] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [showTaskHistoryModal, setShowTaskHistoryModal] = useState(false);
  const [selectedTaskForHistory, setSelectedTaskForHistory] = useState(null);
  const [taskHistoryData, setTaskHistoryData] = useState([]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [showResolvedModal, setShowResolvedModal] = useState(false);

  const { user } = useAuth();
  const currentUserId = user?.user_id ?? null;

  const fetchData = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error('No authentication token found');
        setIsLoadingData(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      setIsLoadingData(true);
      const [alertsRes, patientsRes, cliniciansRes, tasksRes, resolvedAlertsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/alerts/?limit=1000`, { headers }),
        fetch(`${API_BASE_URL}/patients/`, { headers }),
        fetch(`${API_BASE_URL}/clinicians/`, { headers }),
        fetch(`${API_BASE_URL}/tasks/?summary=true&limit=1000`, { headers }),
        fetch(`${API_BASE_URL}/resolved_alerts/`, { headers })
      ]);

      const alertsData = alertsRes.ok ? await alertsRes.json() : [];
      const patientsData = patientsRes.ok ? await patientsRes.json() : [];
      const cliniciansData = cliniciansRes.ok ? await cliniciansRes.json() : [];
      const tasksData = tasksRes.ok ? await tasksRes.json() : [];
      const resolvedAlertsData = resolvedAlertsRes.ok ? await resolvedAlertsRes.json() : [];

      const patientsMap = {};
      patientsData.forEach(p => {
        patientsMap[p.MRN] = {
          id: p.id,
          name: `${p.firstName} ${p.lastName}`
        };
      });

      const doctorsData = cliniciansData.filter(c => c.role === 'Doctor');

      setPatients(patientsMap);

      const activeTasks = tasksData.filter(task => {
        const status = (task.status || '').toLowerCase();
        return status !== 'resolved';
      });

      setAlerts(alertsData);
      setResolvedAlerts(resolvedAlertsData);
      setDoctors(doctorsData);
      setTasks(activeTasks);
      try {
        window.dispatchEvent(new CustomEvent('vitra-alerts-changed'));
      } catch (_) {
    }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoadingData(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddAssignment = async (alertId) => {
    if (!selectedDoctorToAdd) {
      alert('Please select a doctor');
      return;
    }

    const clinicianId = parseInt(selectedDoctorToAdd, 10);

    if (isNaN(clinicianId) || clinicianId <= 0) {
      alert('Invalid doctor selection');
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/alerts/${alertId}/assign/add?clinician_id=${clinicianId}&assigned_by=${currentUserId}`,
        { method: 'POST' }
      );

      if (res.ok) {
        const updatedAlert = await res.json();
        setAlerts(prev => prev.map(a => a.id === alertId ? updatedAlert : a));
        if (selectedAlert?.id === alertId) {
          setSelectedAlert(updatedAlert);
        }
        setSelectedDoctorToAdd('');
        try {
          window.dispatchEvent(new CustomEvent('vitra-alerts-changed'));
        } catch (_) {
    }
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to add assignment');
      }
    } catch (err) {
      console.error('Error adding assignment:', err);
      alert('Error adding assignment');
    }
  };

  const handleRemoveAssignment = async (alertId, clinicianId) => {
    if (!window.confirm('Remove this doctor from the alert?')) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/alerts/${alertId}/assign/${clinicianId}?removed_by=${currentUserId}`,
        { method: 'DELETE' }
      );

      if (res.ok) {
        const result = await res.json();
        const updatedAlert = result.alert;
        setAlerts(prev => prev.map(a => a.id === alertId ? updatedAlert : a));
        if (selectedAlert?.id === alertId) {
          setSelectedAlert(updatedAlert);
        }
        try {
          window.dispatchEvent(new CustomEvent('vitra-alerts-changed'));
        } catch (_) {
    }
      }
    } catch (err) {
      console.error('Error removing assignment:', err);
    }
  };

  const handleAddRecommendation = async (taskId) => {
    const text = newRecommendation[taskId];
    if (!text?.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/tasks/${taskId}/recommendations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim(), created_by: currentUserId })
      });

      if (res.ok) {
        const newRec = await res.json();
        setTasks(prev => prev.map(t =>
          t.id === taskId
            ? { ...t, recommendations: [...(t.recommendations || []), newRec] }
            : t
        ));
        setNewRecommendation(prev => ({ ...prev, [taskId]: '' }));
      }
    } catch (err) {
      console.error('Error adding recommendation:', err);
    }
  };

  const handleUpdateRecommendation = async (taskId, recId) => {
    if (!editingText?.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/tasks/${taskId}/recommendations/${recId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: editingText.trim(), updated_by: currentUserId })
      });

      if (res.ok) {
        const updated = await res.json();
        setTasks(prev => prev.map(t =>
          t.id === taskId
            ? { ...t, recommendations: t.recommendations.map(r => r.id === recId ? updated : r) }
            : t
        ));
        setEditingRecommendation(null);
        setEditingText('');
      }
    } catch (err) {
      console.error('Error updating recommendation:', err);
    }
  };

  const handleDeleteRecommendation = async (taskId, recId) => {
    if (!window.confirm('Delete this recommendation?')) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/tasks/${taskId}/recommendations/${recId}?deleted_by=${currentUserId}`,
        { method: 'DELETE' }
      );

      if (res.ok) {
        setTasks(prev => prev.map(t =>
          t.id === taskId
            ? { ...t, recommendations: t.recommendations.filter(r => r.id !== recId) }
            : t
        ));
      }
    } catch (err) {
      console.error('Error deleting recommendation:', err);
    }
  };

  const handleAddNote = async (taskId) => {
    const text = newNote[taskId];
    if (!text?.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/tasks/${taskId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim(), created_by: currentUserId })
      });

      if (res.ok) {
        const newNoteData = await res.json();
        setTasks(prev => prev.map(t =>
          t.id === taskId
            ? { ...t, notes: [...(t.notes || []), newNoteData] }
            : t
        ));
        setNewNote(prev => ({ ...prev, [taskId]: '' }));
      }
    } catch (err) {
      console.error('Error adding note:', err);
    }
  };

  const handleUpdateNote = async (taskId, noteId) => {
    if (!editingText?.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/tasks/${taskId}/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: editingText.trim(), updated_by: currentUserId })
      });

      if (res.ok) {
        const updated = await res.json();
        setTasks(prev => prev.map(t =>
          t.id === taskId
            ? { ...t, notes: t.notes.map(n => n.id === noteId ? updated : n) }
            : t
        ));
        setEditingNote(null);
        setEditingText('');
      }
    } catch (err) {
      console.error('Error updating note:', err);
    }
  };

  const handleDeleteNote = async (taskId, noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/tasks/${taskId}/notes/${noteId}?deleted_by=${currentUserId}`,
        { method: 'DELETE' }
      );

      if (res.ok) {
        setTasks(prev => prev.map(t =>
          t.id === taskId
            ? { ...t, notes: t.notes.filter(n => n.id !== noteId) }
            : t
        ));
      }
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${API_BASE_URL}/tasks/${taskId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          updated_by: currentUserId
        })
      });

      if (res.ok) {
        if (newStatus === 'Resolved') {
          const task = tasks.find(t => t.id === taskId);

          setTasks(prev => prev.filter(t => t.id !== taskId));
          if (task?.alert_id) {
            setAlerts(prev => prev.filter(a => a.id !== task.alert_id));
          }
        } else {
          setTasks(prev => prev.map(t =>
            t.id === taskId ? { ...t, status: newStatus } : t
          ));
        }
        try {
          window.dispatchEvent(new CustomEvent('vitra-alerts-changed'));
        } catch (_) {
    }
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to update task status');
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      alert('Error updating task status');
    }
  };

  const handleShowTaskHistory = async (task) => {
    try {
      const res = await fetch(`${API_BASE_URL}/tasks/${task.id}/history`);
      if (res.ok) {
        const history = await res.json();
        setTaskHistoryData(history);
        setSelectedTaskForHistory(task);
        setShowTaskHistoryModal(true);
      }
    } catch (err) {
      console.error('Error fetching task history:', err);
    }
  };

  const filteredAlerts = React.useMemo(() => {
    return alerts.filter(alert => {
      if (filters.patientname) {
        const patientId = patients[alert.PMRN]?.id;
        if (patientId !== parseInt(filters.patientname, 10)) return false;
      }

      if (filters.alertDate) {
        const date = parseBackendDate(alert.created_at);
        if (!date) return false;
        const alertDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        if (alertDate !== filters.alertDate) return false;
      }

      if (filters.assignedTo) {
        const selectedDoctorId = parseInt(filters.assignedTo, 10);
        const hasDoctor = (alert.assignments || []).some(asn => {
          const assignedId = asn.clinician_id ?? asn.user_id;
          return assignedId === selectedDoctorId;
        });
        if (!hasDoctor) return false;
      }

      return true;
    });
  }, [alerts, filters, patients]);

  const filteredTasksForAnalytics = React.useMemo(() => {
    return tasks.filter(task => {
      if (filters.patientname) {
        const patientId = patients[task.PMRN]?.id;
        if (patientId !== parseInt(filters.patientname, 10)) return false;
      }

      if (filters.alertDate) {
        const taskDate = parseBackendDate(task.created_at || task.updated_at);
        if (!taskDate) return false;
        const formattedTaskDate = `${taskDate.getFullYear()}-${String(taskDate.getMonth() + 1).padStart(2, '0')}-${String(taskDate.getDate()).padStart(2, '0')}`;
        if (formattedTaskDate !== filters.alertDate) return false;
      }

      if (filters.assignedTo) {
        const selectedDoctorId = parseInt(filters.assignedTo, 10);
        const linkedAlert = alerts.find(a => a.id === task.alert_id);
        const hasDoctor = (linkedAlert?.assignments || []).some(asn => {
          const assignedId = asn.clinician_id ?? asn.user_id;
          return assignedId === selectedDoctorId;
        });
        if (!hasDoctor) return false;
      }

      return true;
    });
  }, [tasks, filters, patients, alerts]);

  const filteredResolvedAlerts = React.useMemo(() => {
    return resolvedAlerts.filter(alert => {
      if (filters.patientname) {
        const patientId = patients[alert.PMRN]?.id;
        if (patientId !== parseInt(filters.patientname, 10)) return false;
      }

      if (filters.alertDate) {
        const resolvedDate = parseBackendDate(alert.resolved_at || alert.created_at);
        if (!resolvedDate) return false;
        const formattedResolvedDate = `${resolvedDate.getFullYear()}-${String(resolvedDate.getMonth() + 1).padStart(2, '0')}-${String(resolvedDate.getDate()).padStart(2, '0')}`;
        if (formattedResolvedDate !== filters.alertDate) return false;
      }

      return true;
    });
  }, [resolvedAlerts, filters, patients]);

  const groupedAlerts = React.useMemo(() => {
    const groups = {};

    filteredAlerts.forEach(alert => {
      const mrn = alert.PMRN;
      const patient = patients[mrn];

      if (!groups[mrn]) {
        groups[mrn] = {
          mrn,
          patientId: patient?.id || null,
          patientName: patient?.name || `Unknown Patient (${mrn})`,
          alerts: [],
          highestPriority: alert.priority || 'Normal'
        };
      }

      groups[mrn].alerts.push(alert);

      if (priorityRank[alert.priority] > priorityRank[groups[mrn].highestPriority]) {
        groups[mrn].highestPriority = alert.priority;
      }
    });

    Object.values(groups).forEach(group => {
      group.alerts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });

    return Object.values(groups);
  }, [filteredAlerts, patients]);

  const analytics = React.useMemo(() => {
    const resolved = filteredResolvedAlerts.length;

    const active = filteredAlerts.length;

    const acknowledged = filteredTasksForAnalytics.filter(task =>
      (task.status || '').toLowerCase().includes('acknowledge')
    ).length;

    return {
      totalPatientsWithAlerts: new Set(filteredAlerts.map(a => a.PMRN)).size,
      active,
      acknowledged,
      resolved
    };
  }, [filteredAlerts, filteredTasksForAnalytics, filteredResolvedAlerts]);

  const formatDate = (dateString) => {
    const date = parseBackendDate(dateString);
    if (!date) return "";
    return `${String(date.getDate()).padStart(2, "0")} ${date.toLocaleString("en-US", { month: "short" })} ${date.getFullYear()}`;
  };

  const formatTime = (dateString) => {
    const date = parseBackendDate(dateString);
    if (!date) return "";
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return `${formatDate(dateString)} ${formatTime(dateString)}`;
  };

  const formatAlertSource = (source) => {
    const sourceText = (source || '').toLowerCase().trim();

    if (sourceText.includes('apple')) return 'Apple Health';
    if (
      sourceText.includes('health connect') ||
      sourceText.includes('health-connect') ||
      sourceText.includes('health_connect') ||
      sourceText.includes('health coonect') ||
      sourceText.includes('healthcoonect')
    ) {
      return 'Health Connect';
    }
    if (sourceText.includes('omron')) return 'Omron Device';
    if (sourceText.includes('manual')) return 'Manual';
    return source || 'Manual';
  };

  const getPriorityColor = (priority) => {
    const normalizedPriority = (priority || '').toLowerCase();
    switch (normalizedPriority) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high':
      case 'high risk': return 'bg-orange-500 text-white';
      case 'moderate': return 'bg-yellow-400 text-gray-900';
      case 'abnormal': return 'bg-rose-500 text-white';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getActionColor = (action) => {
    if (action?.includes('added')) return 'text-green-600';
    if (action?.includes('removed') || action?.includes('deleted')) return 'text-red-600';
    if (action?.includes('updated')) return 'text-blue-600';
    return 'text-gray-600';
  };

  const handleRowHover = (event, mrn) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const popupHeight = 400;
    const popupWidth = 650;
    const spaceBelow = window.innerHeight - rect.bottom;
    const shouldShowAbove = spaceBelow < popupHeight && rect.top > popupHeight;
    const spaceRight = window.innerWidth - rect.left;
    const leftPosition = spaceRight < popupWidth ? Math.max(10, rect.right - popupWidth) : rect.left;

    setPopupPosition({
      top: shouldShowAbove ? rect.top - 10 : rect.bottom + 10,
      left: leftPosition,
      showAbove: shouldShowAbove
    });
    setHoveredPatient(mrn);
  };

  const handlePatientClick = (id) => {
    if (id) navigate(`/patient/${id}`, { state: { activeTab: "alerts" } });
  };

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const getAvailableDoctors = (alertObj) => {
    if (!alertObj) return doctors;

    const assignedClinicianIds = (alertObj.assignments || []).map(a => a.clinician_id);

    return doctors
      .filter(d => d.id && !assignedClinicianIds.includes(d.id))
      .map(d => ({ ...d, id: parseInt(d.id, 10) }));
  };

  const hoveredGroup = groupedAlerts.find(g => g.mrn === hoveredPatient);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedTasks = () => {
    if (!sortConfig.key) return tasks;

    const sortedTasks = [...tasks].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'patient':
          aValue = patients[a.PMRN]?.name || 'Unknown';
          bValue = patients[b.PMRN]?.name || 'Unknown';
          break;
        case 'priority':
          const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          aValue = a.status || 'Pending';
          bValue = b.status || 'Pending';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sortedTasks;
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <div className="inline-flex flex-col ml-1 opacity-30">
          <ChevronUp className="w-3 h-3 -mb-1" />
          <ChevronDown className="w-3 h-3" />
        </div>
      );
    }

    return (
      <div className="inline-flex ml-1">
        {sortConfig.direction === 'asc' ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </div>
    );
  };

  const SkeletonAlertsTable = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
      <div className="px-6 py-4 bg-teal-600">
        <div className="h-6 bg-teal-500 rounded w-32"></div>
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: '600px', overflowY: 'auto' }}>
        <table className="w-full">
          <thead className="bg-gray-100 border-b-2 border-teal-500 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3"><div className="h-4 bg-gray-200 rounded w-20"></div></th>
              <th className="px-6 py-3"><div className="h-4 bg-gray-200 rounded w-16"></div></th>
              <th className="px-6 py-3"><div className="h-4 bg-gray-200 rounded w-12"></div></th>
              <th className="px-6 py-3"><div className="h-4 bg-gray-200 rounded w-24"></div></th>
              <th className="px-6 py-3"><div className="h-4 bg-gray-200 rounded w-16"></div></th>
              <th className="px-6 py-3"><div className="h-4 bg-gray-200 rounded w-16"></div></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {[...Array(5)].map((_, i) => (
              <tr key={i}>
                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-20"></div></td>
                <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-8"></div></td>
                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const SkeletonTasksTable = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="px-6 py-4 bg-indigo-600 flex justify-between items-center">
        <div className="h-6 bg-indigo-500 rounded w-32"></div>
        <div className="h-10 bg-indigo-500 rounded w-32"></div>
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: '700px', overflowY: 'auto' }}>
        <table className="w-full">
          <thead className="bg-gray-100 border-b-2 border-indigo-500 sticky top-0 z-10">
            <tr>
              {[...Array(8)].map((_, i) => (
                <th key={i} className="px-4 py-3">
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {[...Array(6)].map((_, i) => (
              <tr key={i}>
                {[...Array(8)].map((_, j) => (
                  <td key={j} className="px-4 py-4">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col vitra-atmosphere">
      <Header />

      <main className="flex-1 w-full relative">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
            <InlineLoading show={isLoadingData} label="Updating alerts…" />
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 transition-opacity duration-200 ${isLoadingData ? 'opacity-70' : 'opacity-100'}`}>
              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-teal-500 transition-[box-shadow] duration-200 hover:shadow-xl active:scale-[0.99]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Patients with Alerts</p>
                    <p className="text-3xl font-bold text-teal-600 mt-2">
                      <AnimatedNumber value={analytics.totalPatientsWithAlerts} />
                    </p>
                  </div>
                  <Users className="w-10 h-10 text-teal-500" />
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500 transition-[box-shadow] duration-200 hover:shadow-xl active:scale-[0.99]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Active Alerts</p>
                    <p className="text-3xl font-bold text-red-600 mt-2">
                      <AnimatedNumber value={analytics.active} />
                    </p>
                  </div>
                  <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500 transition-[box-shadow] duration-200 hover:shadow-xl active:scale-[0.99]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Acknowledged Alerts</p>
                    <p className="text-3xl font-bold text-yellow-600 mt-2">
                      <AnimatedNumber value={analytics.acknowledged} />
                    </p>
                  </div>
                  <CheckCircle2 className="w-10 h-10 text-yellow-500" />
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-teal-500 transition-[box-shadow] duration-200 hover:shadow-xl active:scale-[0.99]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Resolved Alerts</p>
                    <p className="text-3xl font-bold text-teal-600 mt-2">
                      <AnimatedNumber value={analytics.resolved} />
                    </p>
                  </div>
                  <ShieldCheck className="w-10 h-10 text-teal-500" />
                </div>
              </div>
          </div>

          
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Patient Name</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                  value={filters.patientname}
                  onChange={(e) => setFilters(f => ({ ...f, patientname: e.target.value }))}
                  disabled={isLoadingData}
                >
                  <option value="">All Patients</option>
                  {Object.values(patients).map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alert Date</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                  value={filters.alertDate}
                  onChange={(e) => setFilters(f => ({ ...f, alertDate: e.target.value }))}
                  disabled={isLoadingData}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Doctor</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                  value={filters.assignedTo}
                  onChange={(e) => setFilters(f => ({ ...f, assignedTo: e.target.value }))}
                  disabled={isLoadingData}
                >
                  <option value="">All Doctors</option>
                  {doctors.map(doc => (
                    <option key={doc.id} value={doc.id}>
                      Dr. {doc.firstName} {doc.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {(filters.patientname || filters.alertDate || filters.assignedTo) && (
              <button
                onClick={() => setFilters({ patientname: '', alertDate: '', assignedTo: '' })}
                className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-300"
                disabled={isLoadingData}
              >
                Clear Filters
              </button>
            )}
          </div>

          
          {isLoadingData && groupedAlerts.length === 0 ? (
            <SkeletonAlertsTable />
          ) : (
          <div className={`bg-white rounded-2xl shadow-lg border border-teal-100 overflow-hidden mb-8 relative transition-opacity duration-200 ${isLoadingData ? 'opacity-60' : 'opacity-100'}`}>
          <div className="px-6 py-4 bg-gradient-to-r from-teal-600 to-cyan-600">
            <h2 className="text-xl font-semibold text-white">Patient Alerts</h2>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <table className="w-full">
              <thead className="bg-teal-50 border-b-2 border-teal-200 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Count</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Time</th>
                  
                </tr>
              </thead>
              <tbody className="divide-y divide-teal-50">
                {groupedAlerts.map(group => {
                  const latestAlert = group.alerts[0];

                  return (
                    <tr
                      key={group.mrn}
                      className="hover:bg-teal-50 transition-colors cursor-pointer"
                      onClick={() => handlePatientClick(group.patientId)}
                      onMouseEnter={(e) => handleRowHover(e, group.mrn)}
                      onMouseLeave={() => setHoveredPatient(null)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <User className="w-5 h-5 text-teal-600" />
                          <span className="font-medium text-gray-800">{group.patientName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getPriorityColor(latestAlert.priority)}`}>
                          {latestAlert.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-teal-100 text-teal-700 rounded-full font-bold">
                          {group.alerts.length}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{formatAlertSource(latestAlert.source)}</td>
                      <td className="px-6 py-4 text-gray-600">{formatDate(latestAlert.created_at)}</td>
                      <td className="px-6 py-4 text-gray-600">{formatTime(latestAlert.created_at)}</td>
                      

                      
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>
          )}

          
          {isLoadingData && tasks.length === 0 ? (
            <SkeletonTasksTable />
          ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-indigo-100 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Clinical Tasks</h2>
            <button
              onClick={() => setShowResolvedModal(true)}
              className="bg-white text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-sm font-medium"
            >
              View Resolved Alerts
            </button>

          </div>
          <div className="overflow-x-auto" style={{ maxHeight: '700px', overflowY: 'auto' }}>
            <table className="w-full">
              <thead className="bg-indigo-50 border-b-2 border-indigo-200 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase w-16">
                    S.No
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase w-16 cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center">
                      ID
                      <SortIcon columnKey="id" />
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleSort('patient')}
                  >
                    <div className="flex items-center">
                      Patient
                      <SortIcon columnKey="patient" />
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleSort('priority')}
                  >
                    <div className="flex items-center">
                      Priority
                      <SortIcon columnKey="priority" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase min-w-[280px]">
                    Recommendations
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase min-w-[280px]">
                    Notes
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      <SortIcon columnKey="status" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase w-24">
                    History
                  </th>
                  
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                
                {getSortedTasks().map((task, index) => {
                  const isExpanded = expandedTasks[task.id];
                  const recommendations = task.recommendations || [];
                  const notes = task.notes || [];
                  const taskDescription = task.actionRequired;
                  return (
                    <React.Fragment key={task.id}>
                      
                      <tr className="hover:bg-indigo-50 transition-colors" title={taskDescription}>
                        <td className="px-4 py-4 text-sm font-medium text-gray-800">{index + 1}</td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-800">{task.id}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {patients[task.PMRN]?.name || 'Unknown'}
                        </td>
                        
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-2">
                            
                            {recommendations.length > 0 ? (
                              <div className="text-sm text-gray-700">
                                {recommendations[0].text.substring(0, 40)}
                                {recommendations[0].text.length > 40 && '...'}
                                {recommendations.length > 1 && (
                                  <span className="text-indigo-600 ml-2 font-medium">+{recommendations.length - 1} more</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm italic">No recommendations</span>
                            )}
                            
                            <div className="flex gap-2">
                              <input
                                type="text"
                                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="Add recommendation..."
                                value={newRecommendation[task.id] || ''}
                                onChange={(e) => setNewRecommendation(p => ({ ...p, [task.id]: e.target.value }))}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddRecommendation(task.id)}
                              />
                              <button
                                onClick={() => handleAddRecommendation(task.id)}
                                className="bg-indigo-600 text-white px-2 py-1 rounded text-sm hover:bg-indigo-700 transition-colors"
                                title="Add"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-2">
                            
                            {notes.length > 0 ? (
                              <div className="text-sm text-gray-700">
                                {notes[0].text.substring(0, 40)}
                                {notes[0].text.length > 40 && '...'}
                                {notes.length > 1 && (
                                  <span className="text-indigo-600 ml-2 font-medium">+{notes.length - 1} more</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm italic">No notes</span>
                            )}
                            
                            <div className="flex gap-2">
                              <input
                                type="text"
                                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="Add note..."
                                value={newNote[task.id] || ''}
                                onChange={(e) => setNewNote(p => ({ ...p, [task.id]: e.target.value }))}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddNote(task.id)}
                              />
                              <button
                                onClick={() => handleAddNote(task.id)}
                                className="bg-indigo-600 text-white px-2 py-1 rounded text-sm hover:bg-indigo-700 transition-colors"
                                title="Add"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <select
                            className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            value={task.status || 'Pending'}
                            onChange={(e) => handleUpdateTaskStatus(task.id, e.target.value)}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Acknowledged">Acknowledged</option>
                            <option value="Resolved">Resolved</option>
                          </select>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleTaskExpand(task.id)}
                              className="text-indigo-600 hover:text-indigo-800 transition-colors"
                              title={isExpanded ? "Collapse" : "Expand"}
                            >
                              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                            </button>
                            <button
                              onClick={() => handleShowTaskHistory(task)}
                              className="text-gray-600 hover:text-indigo-600 transition-colors"
                              title="View History"
                            >
                              <History className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan="9" className="px-4 py-4">
                            <div className="grid grid-cols-2 gap-6">
                              
                              <div>
                                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                                  <Bell className="w-4 h-4 mr-2 text-indigo-600" />
                                  All Recommendations ({recommendations.length})
                                </h4>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                  {recommendations.length === 0 ? (
                                    <p className="text-gray-500 text-sm italic">No recommendations yet</p>
                                  ) : (
                                    recommendations.map(rec => (
                                      <div key={rec.id} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                        {editingRecommendation === `${task.id}-${rec.id}` ? (
                                          <div className="space-y-2">
                                            <textarea
                                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500"
                                              value={editingText}
                                              onChange={(e) => setEditingText(e.target.value)}
                                              rows="2"
                                              autoFocus
                                            />
                                            <div className="flex gap-2">
                                              <button
                                                onClick={() => handleUpdateRecommendation(task.id, rec.id)}
                                                className="flex items-center text-green-600 hover:text-green-800 text-sm"
                                              >
                                                <Save className="w-4 h-4 mr-1" /> Save
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setEditingRecommendation(null);
                                                  setEditingText('');
                                                }}
                                                className="flex items-center text-gray-600 hover:text-gray-800 text-sm"
                                              >
                                                <X className="w-4 h-4 mr-1" /> Cancel
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                              <p className="text-sm text-gray-700">{rec.text}</p>
                                              <p className="text-xs text-gray-500 mt-1">
                                                By: {rec.created_by_name || 'Unknown'} | {formatDateTime(rec.created_at)}
                                                {rec.updated_at && (
                                                  <span className="text-blue-600"> (Edited)</span>
                                                )}
                                              </p>
                                            </div>
                                            <div className="flex gap-1 ml-2">
                                              <button
                                                onClick={() => {
                                                  setEditingRecommendation(`${task.id}-${rec.id}`);
                                                  setEditingText(rec.text);
                                                }}
                                                className="text-blue-600 hover:text-blue-800 p-1"
                                                title="Edit"
                                              >
                                                <Edit2 className="w-4 h-4" />
                                              </button>
                                              <button
                                                onClick={() => handleDeleteRecommendation(task.id, rec.id)}
                                                className="text-red-600 hover:text-red-800 p-1"
                                                title="Delete"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>

                              
                              <div>
                                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                                  <Clock className="w-4 h-4 mr-2 text-indigo-600" />
                                  All Notes ({notes.length})
                                </h4>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                  {notes.length === 0 ? (
                                    <p className="text-gray-500 text-sm italic">No notes yet</p>
                                  ) : (
                                    notes.map(note => (
                                      <div key={note.id} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                        {editingNote === `${task.id}-${note.id}` ? (
                                          <div className="space-y-2">
                                            <textarea
                                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500"
                                              value={editingText}
                                              onChange={(e) => setEditingText(e.target.value)}
                                              rows="2"
                                              autoFocus
                                            />
                                            <div className="flex gap-2">
                                              <button
                                                onClick={() => handleUpdateNote(task.id, note.id)}
                                                className="flex items-center text-green-600 hover:text-green-800 text-sm"
                                              >
                                                <Save className="w-4 h-4 mr-1" /> Save
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setEditingNote(null);
                                                  setEditingText('');
                                                }}
                                                className="flex items-center text-gray-600 hover:text-gray-800 text-sm"
                                              >
                                                <X className="w-4 h-4 mr-1" /> Cancel
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                              <p className="text-sm text-gray-700">{note.text}</p>
                                              <p className="text-xs text-gray-500 mt-1">
                                                By: {note.created_by_name || 'Unknown'} | {formatDateTime(note.created_at)}
                                                {note.updated_at && (
                                                  <span className="text-blue-600"> (Edited)</span>
                                                )}
                                              </p>
                                            </div>
                                            <div className="flex gap-1 ml-2">
                                              <button
                                                onClick={() => {
                                                  setEditingNote(`${task.id}-${note.id}`);
                                                  setEditingText(note.text);
                                                }}
                                                className="text-blue-600 hover:text-blue-800 p-1"
                                                title="Edit"
                                              >
                                                <Edit2 className="w-4 h-4" />
                                              </button>
                                              <button
                                                onClick={() => handleDeleteNote(task.id, note.id)}
                                                className="text-red-600 hover:text-red-800 p-1"
                                                title="Delete"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
            {showResolvedModal && (
              <ResolvedAlertsModal onClose={() => setShowResolvedModal(false)} />
            )}
          </div>
          )}
        </div>
      </main>

      
      {hoveredPatient && hoveredGroup && (
        <div
          className="fixed z-50 bg-white border-2 border-teal-500 rounded-lg shadow-2xl p-4"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            width: '650px',
            maxWidth: 'calc(100vw - 20px)',
            maxHeight: '400px',
            transform: popupPosition.showAbove ? 'translateY(-100%)' : 'none'
          }}
          onMouseEnter={() => setHoveredPatient(hoveredPatient)}
          onMouseLeave={() => setHoveredPatient(null)}
        >
          <h4 className="font-bold text-gray-800 mb-3 flex items-center">
            <Bell className="w-4 h-4 mr-2 text-teal-600" />
            All Alerts for {hoveredGroup.patientName}
          </h4>
          <div className="space-y-3 overflow-y-auto" style={{ maxHeight: '320px' }}>
            {hoveredGroup.alerts.map(alert => (
              <div key={alert.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-semibold">Alert ID:</span> {alert.id}</div>
                  <div><span className="font-semibold">Title:</span> {alert.title}</div>
                  <div>
                    <span className="font-semibold">Priority:</span>{' '}
                    <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(alert.priority)}`}>
                      {alert.priority}
                    </span>
                  </div>
                  <div><span className="font-semibold">Date:</span> {formatDateTime(alert.created_at)}</div>
                  <div><span className="font-semibold">Description:</span>{alert.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      
      {selectedAlert && (
      <Modal
        open={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        align="start"
      >
          <div className="bg-white rounded-2xl shadow-2xl border border-sky-100 w-full max-w-lg mx-4 max-h-[min(90vh,720px)] overflow-hidden flex flex-col">
            <DialogHeader
              title="Manage Assignments"
              subtitle="Add or remove doctors assigned to this alert."
              onClose={() => setShowAssignmentModal(false)}
              tone="soft"
            />

            <DialogBody className="p-6">
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Current Assignments</h4>
                {(selectedAlert.assignments || []).length === 0 ? (
                  <p className="text-gray-500 text-sm italic">No doctors assigned yet</p>
                ) : (
                  <div className="space-y-2">
                    {selectedAlert.assignments.map((asn, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg"
                      >
                        <span className="text-sm text-blue-800">{asn.doctor_name}</span>
                        <button
                          onClick={() => handleRemoveAssignment(selectedAlert.id, asn.clinician_id)}
                          className="text-red-500 hover:text-red-700"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Add Doctor</h4>
                <div className="flex gap-2">
                  <select
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={selectedDoctorToAdd}
                    onChange={(e) => setSelectedDoctorToAdd(e.target.value)}
                  >
                    <option value="">Select a doctor...</option>
                    {getAvailableDoctors(selectedAlert).map(doc => (
                      <option key={doc.id} value={doc.id}>
                        Dr. {doc.firstName} {doc.lastName}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleAddAssignment(selectedAlert.id)}
                    disabled={!selectedDoctorToAdd}
                    className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </DialogBody>

            <DialogFooter>
              <button
                type="button"
                onClick={() => setShowAssignmentModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </DialogFooter>
          </div>
      </Modal>
      )}

      
      {selectedAlert && (
      <Modal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        align="start"
      >
          <div className="bg-white rounded-2xl shadow-2xl border border-teal-100 w-full max-w-2xl mx-4 max-h-[84vh] flex flex-col overflow-hidden">
            <DialogHeader
              title={`Alert History - #${selectedAlert.id}`}
              subtitle="Assignment history for this alert."
              onClose={() => setShowHistoryModal(false)}
              tone="soft"
            />

            <DialogBody className="p-6">
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <UserPlus className="w-4 h-4 mr-2 text-blue-600" />
                  Assignment History
                </h4>
                {alertHistory.assignment_history.length === 0 ? (
                  <p className="text-gray-500 text-sm italic">No assignment history</p>
                ) : (
                  <div className="space-y-2">
                    {alertHistory.assignment_history.slice().reverse().map((item, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${getActionColor(item.action)}`}>
                            {item.action === 'added' ? '+ Added' : '- Removed'}: {item.user_name}
                          </span>
                          <span className="text-xs text-gray-500">{formatDateTime(item.timestamp)}</span>
                        </div>
                        {item.by_user_name && (
                          <p className="text-xs text-gray-500 mt-1">By: {item.by_user_name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogBody>

            <DialogFooter>
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </DialogFooter>
          </div>
      </Modal>
      )}

      
      {selectedTaskForHistory && (
      <Modal
        open={showTaskHistoryModal}
        onClose={() => {
          setShowTaskHistoryModal(false);
          setSelectedTaskForHistory(null);
          setTaskHistoryData([]);
        }}
        align="start"
      >
          <div className="bg-white rounded-2xl shadow-2xl border border-indigo-100 w-full max-w-2xl mx-4 max-h-[84vh] flex flex-col overflow-hidden">
            <DialogHeader
              title={`Task History - #${selectedTaskForHistory.id}`}
              subtitle="Changes recorded for this task."
              onClose={() => {
                setShowTaskHistoryModal(false);
                setSelectedTaskForHistory(null);
                setTaskHistoryData([]);
              }}
              tone="muted"
            />

            <DialogBody className="p-6">
              {taskHistoryData.length === 0 ? (
                <p className="text-gray-500 text-sm italic text-center py-8">No history available</p>
              ) : (
                <div className="space-y-3">
                  {taskHistoryData.map((item, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-medium capitalize ${getActionColor(item.action)}`}>
                          {item.action.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-500">{formatDateTime(item.timestamp)}</span>
                      </div>
                      {item.old_value && (
                        <p className="text-sm text-gray-500">
                          <span className="font-medium">Old:</span> {item.old_value}
                        </p>
                      )}
                      {item.new_value && (
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">New:</span> {item.new_value}
                        </p>
                      )}
                      {item.by_user_name && (
                        <p className="text-xs text-gray-500 mt-2">By: {item.by_user_name}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </DialogBody>

            <DialogFooter>
              <button
                type="button"
                onClick={() => {
                  setShowTaskHistoryModal(false);
                  setSelectedTaskForHistory(null);
                  setTaskHistoryData([]);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </DialogFooter>
          </div>
      </Modal>
      )}

      <Footer />
    </div>
  );
};

export default AlertsDashboard;
